import type { LooseCargoDetailInput, LooseCargoResponsibility } from '@chukua/common';
import { Alert, Button, Form } from 'antd';
import { useEffect } from 'react';
import { LooseCargoDetailFields } from './LooseCargoDetailFields';
import type { JobLooseCargoDetails } from '../../../jobs.api';
import type { SpecialHandlingRequirement } from '../../../../reference-data/reference-data.api';

interface DetailsFormValues {
  commodity: string;
  weightKg?: number;
  volumeM3?: number;
  packaging?: string;
  handlingNotes?: string;
  loadingResponsibility?: LooseCargoResponsibility;
  offloadingResponsibility?: LooseCargoResponsibility;
  customerProvidesLabour: boolean;
  specialHandlingRequirementCodes: string[];
  specialHandlingNotes?: string;
}

interface LooseCargoDetailsFormProps {
  details: JobLooseCargoDetails | null;
  specialHandlingRequirements: SpecialHandlingRequirement[];
  error: string | null;
  saving: boolean;
  onSave: (input: LooseCargoDetailInput) => Promise<boolean>;
}

function toFormValues(details: JobLooseCargoDetails | null): Partial<DetailsFormValues> {
  if (!details) return { customerProvidesLabour: false, specialHandlingRequirementCodes: [] };
  return {
    commodity: details.commodity,
    weightKg: details.weightKg ?? undefined,
    volumeM3: details.volumeM3 ?? undefined,
    packaging: details.packaging ?? undefined,
    handlingNotes: details.handlingNotes ?? undefined,
    loadingResponsibility: details.loadingResponsibility ?? undefined,
    offloadingResponsibility: details.offloadingResponsibility ?? undefined,
    customerProvidesLabour: details.customerProvidesLabour,
    specialHandlingRequirementCodes: details.specialHandlingRequirementCodes,
    specialHandlingNotes: details.specialHandlingNotes ?? undefined,
  };
}

function toApiInput(values: DetailsFormValues): LooseCargoDetailInput {
  return {
    commodity: values.commodity,
    weightKg: values.weightKg,
    volumeM3: values.volumeM3,
    packaging: values.packaging || undefined,
    handlingNotes: values.handlingNotes || undefined,
    loadingResponsibility: values.loadingResponsibility,
    offloadingResponsibility: values.offloadingResponsibility,
    customerProvidesLabour: values.customerProvidesLabour,
    specialHandlingRequirementCodes: values.specialHandlingRequirementCodes,
    specialHandlingNotes: values.specialHandlingNotes || undefined,
  };
}

/**
 * Commodity, weight/volume, packaging, responsibility and special handling for a loose-cargo job
 * (PRD → Job intake → Captured per job type (Loose cargo); gitaumoses4/chukua#32) — the commodity
 * is mandatory, everything else fills in as it's learned, same as the job's own fields.
 */
export function LooseCargoDetailsForm({ details, specialHandlingRequirements, error, saving, onSave }: LooseCargoDetailsFormProps) {
  const [form] = Form.useForm<DetailsFormValues>();

  useEffect(() => {
    form.setFieldsValue(toFormValues(details));
  }, [details, form]);

  async function submit(values: DetailsFormValues) {
    await onSave(toApiInput(values));
  }

  return (
    <section className="flex flex-col gap-4">
      <h2 className="text-sm font-semibold text-fg">Cargo details</h2>
      <Form<DetailsFormValues> form={form} layout="vertical" requiredMark={false} onFinish={submit} disabled={saving} initialValues={toFormValues(details)}>
        {error && <Alert className="mb-5" type="error" title={error} showIcon />}
        <LooseCargoDetailFields specialHandlingRequirements={specialHandlingRequirements} />
        <Form.Item className="mb-0">
          <Button type="primary" htmlType="submit" loading={saving}>
            Save details
          </Button>
        </Form.Item>
      </Form>
    </section>
  );
}
